import { useState, useRef, useEffect } from "react";
import { Icon } from "@/components/Icon/Icon";
import { GROUP_OPTIONS } from "./grouping";
import type { GroupKey } from "./grouping";

interface SubstanceGroupByProps {
  value: GroupKey;
  onChange: (g: GroupKey) => void;
}

export function SubstanceGroupBy({ value, onChange }: SubstanceGroupByProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const cur = GROUP_OPTIONS.find((o) => o.id === value) ?? GROUP_OPTIONS[0];

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "6px 10px 6px 11px", borderRadius: 7,
          background: value !== "none" ? "var(--accent-soft)" : "var(--panel)",
          border: `1px solid ${value !== "none" ? "var(--accent-line)" : "var(--line-strong)"}`,
          color: value !== "none" ? "var(--accent-ink)" : "var(--ink-2)", cursor: "pointer",
        }}
      >
        <Icon name="folder" size={13} />
        <span style={{ fontSize: 12 }}>
          <span style={{ color: value !== "none" ? "var(--accent-ink)" : "var(--ink-4)" }}>Group by </span>
          <span style={{ fontWeight: 600 }}>{cur.label}</span>
        </span>
        <span style={{ display: "inline-flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 120ms" }}>
          <Icon name="chevron" size={11} />
        </span>
      </button>
      {open && (
        <div className="fade-in" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 40,
          minWidth: 180, padding: 5, borderRadius: 9,
          background: "var(--panel)", border: "1px solid var(--line-strong)",
          boxShadow: "var(--shadow-3)",
        }}>
          {GROUP_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange(o.id); setOpen(false); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 9,
                padding: "7px 10px", borderRadius: 6, textAlign: "left",
                background: value === o.id ? "var(--accent-soft)" : "transparent",
                color: value === o.id ? "var(--accent-ink)" : "var(--ink-2)",
                fontSize: 12.5, fontWeight: value === o.id ? 600 : 400, cursor: "pointer",
              }}
              onMouseEnter={(e) => { if (value !== o.id) e.currentTarget.style.background = "var(--bg-sunk)"; }}
              onMouseLeave={(e) => { if (value !== o.id) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ width: 14, display: "inline-flex", color: "var(--ink-4)" }}>
                {value === o.id && <Icon name="check" size={12} />}
              </span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
